import React, { useEffect, useState } from 'react';
import { Popover } from '@headlessui/react';
import { motion } from 'framer-motion';
import { CURRENCY_CODE } from '../../config/constants';

export const ToolSelectionWithTotal = ({ tools, state, dispatch, setCharges }) => {
  const [totalPrice, setTotalPrice] = useState(0);

  // Toggle a tool's selection by updating the tool_ids array via dispatch
  const toggleToolSelection = (toolId) => {
    const isSelected = state.tool_ids.includes(toolId);
    let newToolIds;
    if (isSelected) {
      newToolIds = state.tool_ids.filter((id) => id !== toolId);
    } else {
      newToolIds = [...state.tool_ids, toolId];
    }
    dispatch({ type: "UPDATE_FIELD", field: "tool_ids", payload: newToolIds });
  };

  // Calculate the total price of selected tools
  const calculateTotalPrice = () => {
    const currentToolIds = state.tool_ids;
    let newTotalPrice = 0;
    currentToolIds.forEach((toolId) => {
      const tool = tools.find((t) => t.id === toolId);
      if (tool) {
        newTotalPrice += Number(tool.price || 0);
      }
    });
    setTotalPrice(newTotalPrice);
    setCharges((prevData) => ({
      ...prevData,
      tool: newTotalPrice,
    }));
  };

  useEffect(() => {
    calculateTotalPrice();
  }, [state.tool_ids]);

  return (
    <div className="relative p-2 sm:p-4">
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-purple-700 text-center mb-2 sm:mb-4">
        Choose Extra Tools for Your Pet's Walk! 🛠️🐾
      </h2>
      <p className="text-sm sm:text-base text-center text-gray-600 mb-4 sm:mb-6 px-2">
        Add any extra tools to ensure your pet has the best walk experience. Select from leashes, toys, and more! 🐾🎾
      </p>

      {/* Tools Grid - Scrollable on mobile */}
      <div className="relative">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 md:gap-8 justify-items-center">
          {tools.map((tool) => (
            <motion.div
              key={tool.id}
              className="relative w-full"
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 200, damping: 10 }}
            >
              <Popover className="relative flex justify-center">
                <Popover.Button
                  className={`relative w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-full shadow-lg cursor-pointer 
                    transition-all duration-300 ease-in-out flex flex-col items-center justify-center p-2 
                    ${state.tool_ids.includes(tool.id) 
                      ? 'bg-purple-500 text-white scale-105' 
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                  onClick={() => toggleToolSelection(tool.id)}
                >
                  <motion.span
                    className="font-medium text-xs sm:text-sm md:text-base text-center"
                    animate={{ y: [0, -2, 0], transition: { duration: 0.3 } }}
                  >
                    {tool.name}
                  </motion.span>
                  <span className="text-xs mt-1 opacity-75">
                    {tool.price + ' ' + CURRENCY_CODE}
                  </span>
                </Popover.Button>

                {/* Tool Info Popup - Adjusted for mobile */}
                <Popover.Panel className="absolute z-10 w-40 sm:w-48 p-3 bg-white border border-gray-200 
                  rounded-lg shadow-lg transform -translate-y-full translate-x-1 sm:-translate-x-1/4 mt-2">
                  <p className="font-semibold text-sm sm:text-base">{tool.name}</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    {tool.price + ' ' + CURRENCY_CODE}
                  </p>
                  <p className="text-xs sm:text-sm mt-1">{tool.description}</p>
                </Popover.Panel>
              </Popover>

              {/* Selection Indicator */}
              {state.tool_ids.includes(tool.id) && (
                <motion.div
                  className="absolute top-0 right-0 sm:right-4 bg-pink-500 rounded-full p-1 shadow-md"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-4 w-4 sm:h-5 sm:w-5 text-white" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Total Price Section */}
      <motion.div
        className="mt-4 sm:mt-6 p-3 sm:p-4 bg-purple-50 rounded-lg border border-purple-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-center">
          <span className="text-sm sm:text-base font-medium text-gray-700">
            Addon's Total:
          </span>
          <span className="text-base sm:text-lg font-semibold text-purple-600">
            {`${totalPrice.toFixed(2)} ${CURRENCY_CODE}`}
          </span>
        </div>
      </motion.div>
    </div>
  );
};